import React, { useRef, useCallback } from "react";

const uniq = (arr = []) => Array.from(new Set(arr.filter(Boolean)));

const ProductHoverImage = ({ product }) => {
  const [idx, setIdx] = React.useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isSwiping = useRef(false);
  const swipeThreshold = 50; // minimum swipe distance

  const all = React.useMemo(() => {
    return uniq(product?.default_images || []);
  }, [product?.default_images]);

  const hoverEnabled = all.length >= 3;

  const hoverImages = React.useMemo(() => {
    if (!hoverEnabled) return [all[0]].filter(Boolean);

    const first = all[0];
    const last2 = all[all.length - 2];
    const last1 = all[all.length - 1];

    return [first, last2, last1].filter(Boolean);
  }, [all, hoverEnabled]);

  const currentSrc = hoverEnabled ? hoverImages[idx] : all[0];

  // Desktop mouse hover handler
  const handleMove = (e) => {
    if (!hoverEnabled) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(0.9999, x / rect.width));

    const nextIdx = Math.min(2, Math.floor(ratio * 3));
    if (nextIdx !== idx) setIdx(nextIdx);
  };

  const handleLeave = () => {
    if (!hoverEnabled) return;
    setIdx(0);
  };

  // Mobile touch handlers
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    isSwiping.current = false;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndX.current = e.touches[0].clientX;
    const diff = Math.abs(touchStartX.current - touchEndX.current);

    // If user is swiping horizontally, prevent default to stop scrolling and link clicks
    if (diff > 10) {
      isSwiping.current = true;
      e.preventDefault();
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      if (!hoverEnabled) return;

      const diff = touchStartX.current - touchEndX.current;

      if (Math.abs(diff) > swipeThreshold) {
        // Prevent the click event from firing
        e.preventDefault();
        e.stopPropagation();

        if (diff > 0) {
          // Swipe left - next image
          setIdx((prev) => Math.min(2, prev + 1));
        } else {
          // Swipe right - previous image
          setIdx((prev) => Math.max(0, prev - 1));
        }
      }

      isSwiping.current = false;
    },
    [hoverEnabled],
  );

  // Handle dot click
  const handleDotClick = useCallback((e, dotIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx(dotIndex);
  }, []);

  // Handle click on the container - prevent if swiping
  const handleClick = useCallback((e) => {
    if (isSwiping.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  if (!all.length) return null;

  return (
    <div
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}
      style={{
        width: "100%",
        position: "relative",
        touchAction: hoverEnabled ? "pan-y" : "auto",
      }}
    >
      <img
        src={(currentSrc || "") + "?v=123456"}
        alt={product?.name}
        className="w-100"
        loading="lazy"
        style={{ display: "block", userSelect: "none" }}
        draggable={false}
      />

      {/* Dots - clickable on mobile */}
      {hoverEnabled && (
        <div
          className="d-flex justify-content-center gap-2"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 10,
            pointerEvents: "auto",
          }}
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              onClick={(e) => handleDotClick(e, i)}
              style={{
                width: 3,
                height: 3,
                borderRadius: 999,
                background: i === idx ? "#ddd1be" : "#741615",
                display: "inline-block",
                opacity: 0.9,
                cursor: "pointer",
                padding: 4,
                boxSizing: "content-box",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductHoverImage;
